// API Configuration for Chess Deck
//
// All infrastructure URLs are defined here. Update after CDK deploy.

// ============================================================================
// INFRASTRUCTURE URLs — update these after `cdk deploy`
//
// CDK Output Name       → Variable
// HttpApiUrl            → API_BASE
// CoachStreamUrl        → LLM_STREAM_URL
// ============================================================================

import type { GameRecord } from "../types/game";
import { timedFetchJson, withTimeout } from "./withTimeout";
import type { PassThreat } from "../types/engine";
import type {
  GamesListResponse,
  UserProfileApiResponse,
  ShowcaseListResponse,
  FetchPgnRequest,
  SaveGameRequest,
  UpdateUserProfileRequest,
  StreamAskRequest,
  LLMStreamResult,
} from '../types/api';

// Default to the prod API Gateway. Overridable via REACT_APP_API_BASE so a non-3000 dev port can point
// at a same-origin proxy (the gateway's CORS allowlist only includes localhost:3000) — see setupProxy.js.
const API_BASE = process.env.REACT_APP_API_BASE || "https://6i5qrj1ulf.execute-api.us-east-1.amazonaws.com";

/** A request's wait for its token (Clerk: well under 1 s) and its response (API Gateway cuts at 30 s); past them it fails
 *  and its caller's retry runs, not an endless skeleton (2026-11-04, the Mistakes tab's All window). */
const AUTH_TIMEOUT_MS = 10_000, REQUEST_TIMEOUT_MS = 40_000;
// Exported so utils/coachStream.ts shares this exact base URL rather than duplicating it.
// Overridable like API_BASE: the local production-key dev server (scripts/dev-local-prod.sh) reaches it through
// setupProxy.js same-origin, since its CORS allowlist names the app and localhost, not local.chess-deck.com.
export const LLM_STREAM_URL = process.env.REACT_APP_LLM_STREAM_URL || "https://5zmis6ttlrugab3jjvxvabwgla0nlsgq.lambda-url.us-east-1.on.aws/";
// Maia popByElo + maiaWin COORDINATOR (server-side whole-game fan-out; ChessMaiaSnapStack). The frontend
// POSTs the whole game once; the coordinator splits it 1-position-per-invoke across concurrent SnapStart
// pop Lambdas and merges. The fan-out is REQUIRED: a whole-game value_elo scan is ~30-40 positions, and a
// single box can't parallelize them — the t4g EC2 /pop (maia.chess-deck.com, deleted 2026-11-03, knowledge/aws-spend.md)
// measured 60s+ on a whole game (client aborts at the 7s timeout → #131 silently dead), vs the coordinator's ~7s.
// Public Function URL, CORS-allowlisted to the app + localhost.
// Overridable for the local production-key dev server, as LLM_STREAM_URL (a trailing slash: callers append "pop").
export const MAIA_COORD_URL = process.env.REACT_APP_MAIA_COORD_URL || "https://iwibytnxopvsiurwzk2e4olgcq0lzzke.lambda-url.us-east-1.on.aws/";

// ============================================================================
// Types
// ============================================================================

export type GetAuthHeaders = () => Promise<Record<string, string>>;

/** SSE event from the LLM streaming Lambda. */
interface SSEEvent {
  type: 'delta' | 'done' | 'error';
  text?: string;
  message?: string;
  // Extra fields sent by specific routes — kept typed but optional so callers opt in.
  coach_title?: string;
  persisted?: boolean;
  game_origin_id?: string;
  /** On /ask's and /drill's done event: why the model stopped (#314). */
  finish_reason?: string;
  /** On /ask's done event when it was sent a game_origin_id (#334): whether the answer was saved with the game. */
  saved?: boolean;
}

// Request body types live in types/api.ts, next to the response types they mirror — see the import
// at the top of this file.

export interface DebugEntry {
  id: number;
  label: string;
  timestamp: string;
  request: Record<string, unknown>;
  response: unknown;
  status: number | null;
  durationMs: number | null;
  error: string | null;
}

// ============================================================================
// DEBUG LOG — captures all request/response pairs for the Debug tab.
// DEV-ONLY. In production every user's browser would accumulate the last
// 50 request/response pairs (PGNs, coaching outputs, moment details),
// visible to anyone with devtools access. Gated on NODE_ENV.
// ============================================================================

declare global {
  interface Window {
    __apiDebugLog: DebugEntry[];
  }
}

const _DEBUG_LOG_ENABLED = process.env.NODE_ENV !== 'production';

if (_DEBUG_LOG_ENABLED) {
  window.__apiDebugLog = window.__apiDebugLog || [];
}
let _debugId = 0;

/** Maximum debug log entries to keep (ring buffer). */
const MAX_DEBUG_LOG_SIZE = 50;

export const getDebugLog = (): DebugEntry[] =>
  _DEBUG_LOG_ENABLED ? window.__apiDebugLog : [];
export const clearDebugLog = (): void => {
  if (_DEBUG_LOG_ENABLED) window.__apiDebugLog = [];
};

// ============================================================================
// CORE API HELPER (JSON request/response via API Gateway)
// ============================================================================

/** A JSON call's body and its HTTP status, for a caller that tells 2xx codes apart (top games: 202 = still reading).
 *  Non-2xx throws, as apiCall does. */
export interface ApiResponse<T> {
  readonly status: number;
  readonly data: T;
}

export const apiRequest = async <T = unknown>(
  path: string,
  method: string,
  body: object | null,
  getAuthHeaders: GetAuthHeaders | null,
  label: string,
): Promise<ApiResponse<T>> => {
  const url = `${API_BASE}${path}`;
  const start = performance.now();
  const entry: DebugEntry = {
    id: ++_debugId,
    label,
    timestamp: new Date().toISOString(),
    request: (body as Record<string, unknown>) || { path, method },
    response: null,
    status: null,
    durationMs: null,
    error: null,
  };
  if (_DEBUG_LOG_ENABLED) {
    window.__apiDebugLog.push(entry);
    if (window.__apiDebugLog.length > MAX_DEBUG_LOG_SIZE) {
      window.__apiDebugLog.splice(0, window.__apiDebugLog.length - MAX_DEBUG_LOG_SIZE);
    }

    if (body) {
      const logBody: Record<string, unknown> = { ...body };
      if (Array.isArray(logBody.evaluations)) logBody.evaluations = `[${logBody.evaluations.length} evals]`;
      if (Array.isArray(logBody.move_evaluations)) logBody.move_evaluations = `[${logBody.move_evaluations.length} classifications]`;
      if (typeof logBody.pgn === 'string' && logBody.pgn.length > 200) logBody.pgn = logBody.pgn.substring(0, 200) + '...';
      console.log(`=== ${label} REQUEST ===`);
      console.log(JSON.stringify(logBody, null, 2));
    } else {
      console.log(`=== ${label} REQUEST === ${method} ${path}`);
    }
  }

  let response: Response;
  let data: Record<string, unknown>;
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (getAuthHeaders) {
      Object.assign(headers, await withTimeout(getAuthHeaders(), AUTH_TIMEOUT_MS, "AUTH_TOKEN_TIMEOUT"));
    }

    ({ response, data } = await timedFetchJson(url, { method, headers, body: body ? JSON.stringify(body) : undefined }, REQUEST_TIMEOUT_MS));
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    entry.error = message;
    entry.durationMs = Math.round(performance.now() - start);
    throw err;
  }

  entry.status = response.status;
  entry.response = data;
  entry.durationMs = Math.round(performance.now() - start);

  if (_DEBUG_LOG_ENABLED) {
    console.log(`=== ${label} RESPONSE (${response.status}) ===`);
  }

  if (!response.ok) {
    const errData = data as Record<string, unknown>;
    const errorField = errData.error as Record<string, unknown> | string | undefined;
    const errMsg = (typeof errorField === 'object' && errorField?.message)
      ? String(errorField.message)
      : (typeof errorField === 'string' ? errorField : null)
        || (typeof errData.message === 'string' ? errData.message : null)
        || `API error: ${response.status}`;
    entry.error = errMsg;
    const err = new Error(errMsg) as Error & { status: number; data: unknown };
    err.status = response.status;
    err.data = data;

    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('chess-deck:auth-required', {
        detail: { path, label },
      }));
    }

    throw err;
  }

  return { status: response.status, data: data as T };
};

export const apiCall = async <T = unknown>(
  path: string,
  method: string,
  body: object | null,
  getAuthHeaders: GetAuthHeaders | null,
  label: string,
): Promise<T> => (await apiRequest<T>(path, method, body, getAuthHeaders, label)).data;

// ============================================================================
// CORE SSE STREAMING (Lambda Function URL — bypasses API Gateway)
// ============================================================================

/**
 * Generic SSE streaming consumer for the LLM Lambda Function URL (/ask today; /coach-game has its own, coachStream.ts).
 * `onEvent` gets every event (delta, done, error); an error event throws. Resolves to the joined deltas and whether
 * the terminal `done` arrived (#314): `_stream_sse` always sends it, so a body that ends without it was cut off.
 */
const streamFromLLM = async (
  route: string,
  body: object,
  getAuthHeaders: GetAuthHeaders,
  onEvent: (event: SSEEvent) => void,
): Promise<LLMStreamResult> => {
  if (!LLM_STREAM_URL) {
    throw new Error('LLM streaming URL not configured');
  }

  const authHeaders = await getAuthHeaders();
  const url = `${LLM_STREAM_URL.replace(/\/$/, '')}${route}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 402) {
      let errorMsg = 'Usage limit reached';
      try {
        const errData = await response.json();
        errorMsg = errData?.error?.message || errData?.error || errorMsg;
      } catch { /* ignore */ }
      const err = new Error(errorMsg) as Error & { status: number; code: string };
      err.status = 402;
      err.code = 'USAGE_LIMIT';
      throw err;
    }
    throw new Error(`Stream error: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('No response body for streaming');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let fullText = '';
  let buffer = '';
  let ended: LLMStreamResult['ended'] = 'closed';
  let finishReason: string | null = null;
  let saved: boolean | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      // Parse in its own try so malformed chunks are dropped cleanly.
      // Dispatch runs OUTSIDE the try so caller's onEvent errors
      // propagate instead of being silently skipped.
      let event: SSEEvent;
      try {
        event = JSON.parse(jsonStr);
      } catch {
        continue;
      }

      if (event.type === 'delta' && event.text) fullText += event.text;
      if (event.type === 'done') { ended = 'done'; finishReason = event.finish_reason ?? null; saved = event.saved ?? null; }
      onEvent(event);
      if (event.type === 'error') {
        const err = new Error(event.message || 'Stream error') as Error & { code: string };
        err.code = 'STREAM_ERROR';
        throw err;
      }
    }
  }

  return { text: fullText, ended, finishReason, saved };
};

// ============================================================================
// API FUNCTIONS — JSON (via API Gateway)
// ============================================================================

// --- PGN Fetching (public, no auth) ---

export interface FetchPgnResponse {
  pgn: string;
  white: string;
  black: string;
  white_rating?: string;
  black_rating?: string;
  result?: string;
  source?: string;
  gameId?: string;
}

export const fetchPgn = (body: FetchPgnRequest) =>
  apiCall<FetchPgnResponse>('/fetch-pgn', 'POST', body, null, 'FETCH_PGN');

// --- Mistake tags (rule-based tagger; the tagging system that replaces SAE features) ---
// The browser sends the Stockfish lines it already computed; the Lambda runs the tagger.
// Field names match tag_adapter.eval_to_mistake exactly (zero remapping server-side).

export interface TagMomentsRequest {
  readonly moments: ReadonlyArray<{
    readonly ply: number;
    readonly fen_before: string;
    readonly uci: string;
    readonly pv_uci: readonly string[];
    readonly refutation_uci: readonly string[];
    readonly eval: number;
    /** Eval AFTER the played move. Feeds the tagger's win-drop gate — without it the backend can't tell
     *  a real mistake from a fine move, so it emits only phase/info tags (no motif/material tags). */
    readonly eval_after?: number;
    readonly san: string;
    readonly bestMoveSan: string;
    /** Authoritative move classification (blunder/mistake/inaccuracy/...). When present it drives the
     *  tagger's explain gate INSTEAD of win_drop, so inaccuracies get explained on review cards. Kept
     *  out of stats/drills downstream via a classification filter. Omit → tagger uses win_drop>=10. */
    readonly classification?: string;
    /** The position before the opponent's last move, and that move (UCI): the move that led to fen_before. The
     *  verified rules' Poisoned Grab, Autopilot Recapture and Didn't take the free material need it (#247). Omit on the
     *  first move. */
    readonly prev_fen?: string;
    readonly prev_uci?: string;
    /** The pass threat: the opponent's best reply had the player passed, in SAN, and its eval in pawns from WHITE's side
     *  (backend tag_adapter.verified_row). Left It Hanging, Poisoned Grab and Moved a guard need it (#247); omitted in
     *  check. */
    readonly threat?: PassThreat;
  }>;
}

export interface TagMomentsResponse {
  readonly moments: ReadonlyArray<{
    readonly ply: number;
    readonly mistake_tags: ReadonlyArray<{
      label: string; category: string; direction: string;
      evidence?: string; layer?: string;   // the literal why-it-fired (line/target) + verified|tactic|position
    }>;
    /** The board facts the predicates reasoned over — SEE, capture/check flags per ply, what's actually
     *  loose. Present so a tag can be audited from a Debug-tab paste instead of by rebuilding the
     *  position by hand (2026-08-08). Opaque here; only the Debug tab reads it. */
    readonly audit?: Record<string, unknown>;
  }>;
}

export const tagMoments = (body: TagMomentsRequest) =>
  apiCall<TagMomentsResponse>('/tag-moments', 'POST', body, null, 'TAG_MOMENTS');

// --- Games (authenticated) ---

export const saveGame = (body: SaveGameRequest, getAuthHeaders: GetAuthHeaders) =>
  apiCall('/games', 'POST', body, getAuthHeaders, 'SAVE_GAME');

/** GET /games' projections: full (moments), card (the Library), openings (the Openings page). */
export type GamesView = 'full' | 'card' | 'openings' | 'mistakes';

export const listUserGames = async (
  getAuthHeaders: GetAuthHeaders,
  limit = 50,
  nextToken?: string | null,
  view: GamesView = 'full',
  since?: string, // YYYY-MM-DD: only games on or after it (the server narrows its query by date)
) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (nextToken) params.set('nextToken', nextToken);
  // card = light projection (no moment arrays) — 5.6x smaller, for the Library grid. openings = the header fields and
  // the first 24 SANs (`openingSan`), for the Openings page (#235).
  if (view !== 'full') params.set('view', view);
  if (since) params.set('since', since);
  return apiCall<GamesListResponse>(`/games?${params}`, 'GET', null, getAuthHeaders, 'LIST_USER_GAMES');
};

/** One game's full record. The route wraps it: `{ game }` (game_store SingleGameResponse) — read `.game`. */
export const getGame = (gameOriginId: string, getAuthHeaders: GetAuthHeaders) =>
  apiCall<{ game: GameRecord | null }>(`/games/${encodeURIComponent(gameOriginId)}`, 'GET', null, getAuthHeaders, 'GET_GAME');

/** Just the gameOriginIds this player has analyzed — the cheap "is this analyzed?" query.
 *  Backed by the player-games lookup table alone (no analysis records), so it's fast where a full
 *  listUserGames would pull the whole library. Powers the landing "Reviewed" tag. */
export const listAnalyzedIds = (getAuthHeaders: GetAuthHeaders) =>
  apiCall<{ ids: string[] }>(`/games/analyzed-ids`, 'GET', null, getAuthHeaders, 'ANALYZED_IDS');

/** Authenticated cache check — does this game already have analysis? */
export const getAnalysisCache = (gameOriginId: string, getAuthHeaders: GetAuthHeaders) =>
  apiCall(`/analysis/${encodeURIComponent(gameOriginId)}`, 'GET', null, getAuthHeaders, 'ANALYSIS_CACHE');

// --- Bulk analysis job (authenticated) ---

/** Terminal states stop polling — anything else means the worker is still going.
 *  INTERRUPTED: written by the worker's sequential mode on SIGTERM, or derived by GET /analyze-bulk for a running
 *  job nothing has written to for 40 min (a hard-killed task, #304). The job is resumable but not advancing, so the
 *  UI treats it as stopped rather than running. */
export type BackfillJobStatus =
  | 'PENDING' | 'FETCHING' | 'PROCESSING' | 'COMPLETE' | 'FAILED' | 'INTERRUPTED';

export interface BackfillJob {
  jobId: string;
  status: BackfillJobStatus;
  gamesTotal: number;
  gamesComplete: number;
  username?: string | null;
  platform?: string | null;
  errorMessage?: string | null;
  completedAt?: string | null;
  /** ISO 8601, bumped by the worker after EVERY game — the staleness signal for a silently dead task. */
  updatedAt?: string | null;
  /**
   * Why a job completed without analyzing anything. Only set on 0-progress completions.
   *
   * `already_complete` — every game was already in the database. Normal, not an error: analysis is keyed
   * on `gameOriginId` and shared across accounts, so a second user linking the same chess username
   * inherits the whole library instantly.
   * `no_games_found` — the platform returned no games for that username (typo, or empty account).
   */
  outcome?: 'already_complete' | 'no_games_found' | null;
  /** `sync`: the player's new games (#259: the 50 newest since the newest stored one). `backfill`, or absent on older
   *  jobs: the history import. */
  mode?: 'backfill' | 'sync' | null;
}

/** Poll a bulk-analysis job. The backend 404s if the job isn't the caller's. */
export const getBackfillJob = (jobId: string, getAuthHeaders: GetAuthHeaders) =>
  apiCall<BackfillJob>(
    `/analyze-bulk?jobId=${encodeURIComponent(jobId)}`, 'GET', null, getAuthHeaders, 'BACKFILL_JOB',
  );

/** What a sync request did (#259). `jobId` is the user's activeBackfillJobId: the new job (`started`), the one still
 *  going (`running`), or the last one (`recent`: under 60 s since the last sync). */
export interface GameSyncResult {
  status: 'started' | 'running' | 'recent' | 'not_linked' | 'unsupported';
  jobId: string | null;
}

/** Queue the caller's new games for server-side analysis: Stockfish, the move classification and the tagger, never
 *  an LLM. The server picks the account, the window and the cap, and allows one sync every 60 s, so call freely. */
export const startGameSync = (getAuthHeaders: GetAuthHeaders) =>
  apiCall<GameSyncResult>('/analyze-bulk', 'POST', { mode: 'sync' }, getAuthHeaders, 'GAME_SYNC');



// --- Chess Username Linking (authenticated) ---

export const setChessUsername = async (username: string, platform: string, getAuthHeaders: GetAuthHeaders) =>
  apiCall('/usernames', 'POST', { chessUsername: username, platform }, getAuthHeaders, 'SET_CHESS_USERNAME');

// --- Showcase (public, no auth) ---

export const getShowcasePlayers = async () =>
  apiCall<ShowcaseListResponse>('/showcase', 'GET', null, null, 'SHOWCASE_PLAYERS');

export const getShowcasePlayer = async (username: string) =>
  apiCall(`/showcase/${encodeURIComponent(username)}`, 'GET', null, null, 'SHOWCASE_PLAYER');

// --- User Profile (authenticated) ---

export const getUserProfile = async (getAuthHeaders: GetAuthHeaders) =>
  apiCall<UserProfileApiResponse>('/users/me', 'GET', null, getAuthHeaders, 'GET_USER_PROFILE');

export const updateUserProfile = async (body: UpdateUserProfileRequest, getAuthHeaders: GetAuthHeaders) =>
  apiCall('/users/me', 'PUT', body, getAuthHeaders, 'UPDATE_USER_PROFILE');

// ============================================================================
// STREAMING API FUNCTIONS — SSE (via Lambda Function URL)
// ============================================================================
/**
 * Stream the answer to a position question (POST /ask). `onDelta` gets each text chunk as it arrives; resolves to the
 * whole text and how the stream ended, which the Ask card turns into complete / cut off / empty (askOutcome, #314).
 * Throws on a non-200, an error event or a dropped connection (402 carries `code: 'USAGE_LIMIT'`).
 *
 * @param body           — Ask payload (fen, question, player_color, etc.)
 * @param getAuthHeaders — Auth header provider
 */
export const streamAskPosition = async (
  body: StreamAskRequest,
  getAuthHeaders: GetAuthHeaders,
  onDelta: (text: string) => void,
): Promise<LLMStreamResult> => {
  return streamFromLLM('/ask', body, getAuthHeaders, (event) => {
    if (event.type === 'delta' && event.text) {
      onDelta(event.text);
    }
  });
};

// The streamed /coach-game client (CoachStreamEvent, parseCoachSSE, streamCoachGame) lives in
// utils/coachStream.ts — split out when this file passed its size cap. It imports LLM_STREAM_URL +
// GetAuthHeaders from here.

/** POST /telemetry — customer PIPELINE telemetry (bucket ①): fire-and-forget the non-LLM analysis trace
 *  (timings, tag attribution + why, errors) so it lands in CloudWatch tagged by user+game, alongside the
 *  server's COACH_* logs for the same game. Best-effort by design: any failure (offline, 401, timeout) is
 *  swallowed — telemetry must never surface to the user or block the pipeline. No-op when signed out (no
 *  auth header → the server 401s and we drop it). */
export const postTelemetry = async (
  body: { game_origin_id?: string; event?: string; data: object },
  getAuthHeaders: GetAuthHeaders,
): Promise<void> => {
  if (!LLM_STREAM_URL) return;
  try {
    const authHeaders = await getAuthHeaders();
    await fetch(`${LLM_STREAM_URL.replace(/\/$/, '')}/telemetry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(body),
      keepalive: true,  // let it complete even if the user navigates away right after a run
    });
  } catch { /* telemetry is best-effort — never surface or throw */ }
};

/** POST /billing/checkout — create a Stripe Checkout Session, return its hosted URL to redirect to.
 *  Money path: the tier flip happens server-side on the webhook, never here. */
export const startCheckout = async (getAuthHeaders: GetAuthHeaders): Promise<string> => {
  const data = await apiCall<{ url: string }>('/billing/checkout', 'POST', {}, getAuthHeaders, 'CHECKOUT');
  return data.url;
};

/** POST /billing/portal — Stripe Customer Portal (manage/cancel), return its hosted URL. */
export const openBillingPortal = async (getAuthHeaders: GetAuthHeaders): Promise<string> => {
  const data = await apiCall<{ url: string }>('/billing/portal', 'POST', {}, getAuthHeaders, 'PORTAL');
  return data.url;
};
// ============================================================================
// LEGACY EXPORT — still used by gameSync.ts
// ============================================================================

export const GAME_STORE_URL = API_BASE;